import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Search } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { LoginPopover } from '@/components/LoginPopover';
import { ProfileMenu } from '@/components/ProfileMenu';
import video2Asset from '@/assets/nonna-cooking-2.mp4.asset.json';

const Landing = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [searchQuery, setSearchQuery] = useState('');

  
  // Video management - simple instant switch
  const [activeVideo, setActiveVideo] = useState(0);
  const videoRefs = useRef<(HTMLVideoElement | null)[]>([]);
  
  const videos = [
    '/videos/nonna-cooking.mp4',
    video2Asset.url,
    '/videos/nonna-cooking-3.mp4'
  ];

  // Start first video and set playback rate; warm up the rest after first paint
  useEffect(() => {
    videoRefs.current.forEach((video, index) => {
      if (video) {
        video.playbackRate = 0.7;
        if (index === 0) {
          video.play();
        }
      }
    });

    const warmup = window.setTimeout(() => {
      videoRefs.current.forEach((video, index) => {
        if (video && index !== 0) {
          video.preload = "auto";
          video.load();
        }
      });
    }, 4000);

    return () => window.clearTimeout(warmup);
  }, []);


  // Handle video end - instant switch to next
  const handleVideoEnd = () => {
    const nextIndex = (activeVideo + 1) % videos.length;
    setActiveVideo(nextIndex);
    
    const nextVideoEl = videoRefs.current[nextIndex];
    if (nextVideoEl) {
      nextVideoEl.currentTime = 0;
      nextVideoEl.play();
    }
  };

  // Navigation handlers
  const handleNavClick = (path: string) => (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    navigate(path);
  };

  const handleSearch = () => {
    const params = new URLSearchParams();
    if (searchQuery) params.set('search', searchQuery);
    navigate(`/recipes?${params.toString()}`);
  };


  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  return (
    <div className="relative min-h-screen overflow-hidden">
      {/* All videos rendered - only active one visible */}
      {videos.map((src, index) => (
        <video
          key={src}
          ref={(el) => { videoRefs.current[index] = el; }}
          muted
          playsInline
          poster={index === 0 ? "/nonna-poster.jpg" : undefined}
          preload={index === 0 ? "auto" : "none"}
          onEnded={handleVideoEnd}
          className={`absolute inset-0 w-full h-full object-cover z-[1] ${
            index === activeVideo ? 'opacity-100' : 'opacity-0'
          }`}
        >
          <source src={src} type="video/mp4" />
        </video>
      ))}


      {/* Dark Overlay */}
      <div className="absolute inset-0 bg-black/50 z-[2] pointer-events-none" />

      {/* Content Container */}
      <div className="relative z-10 min-h-screen flex flex-col" style={{ zIndex: 10 }}>
        {/* Top Navigation Bar */}
        <header className="flex justify-between items-center p-6 md:p-8 pointer-events-auto relative z-20">
          <div /> {/* Spacer */}
          <nav className="flex items-center gap-6">
            <button 
              onClick={handleNavClick('/')}
              className="text-white transition-colors text-sm font-medium cursor-pointer bg-transparent border-none pb-1 border-b border-white"
            >
              Home
            </button>
            <button 
              onClick={handleNavClick('/recipes')}
              className="text-white/90 hover:text-white transition-colors text-sm font-medium cursor-pointer bg-transparent border-none"
            >
              Recipes
            </button>
            <button 
              onClick={handleNavClick('/meal-plans')}
              className="text-white/90 hover:text-white transition-colors text-sm font-medium cursor-pointer bg-transparent border-none"
            >
              Meal Plans
            </button>
            <button 
              onClick={handleNavClick('/about')}
              className="text-white/90 hover:text-white transition-colors text-sm font-medium cursor-pointer bg-transparent border-none"
            >
              About
            </button>
            {user ? (
              <ProfileMenu />
            ) : (
              <div className="[&_button]:text-white/90 [&_button]:hover:text-white">
                <LoginPopover />
              </div>
            )}
          </nav>
        </header>

        {/* Main Hero Content */}
        <main className="flex-1 flex flex-col items-center justify-center px-4 -mt-16 relative z-10">
          {/* Large White Title */}
          <h1 className="text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-serif text-white text-center mb-6 md:mb-8 tracking-wide italic">
            Cooking with Nonna
          </h1>

          {/* Search */}
          <div className="flex flex-col items-center gap-3 w-full max-w-sm">
            <div className="group relative w-full">
              {/* Soft glow */}
              <div className="absolute -inset-1.5 bg-gradient-to-r from-primary/30 to-primary/10 rounded-full blur opacity-40 group-focus-within:opacity-70 transition duration-500" />

              <div className="relative flex items-center w-full h-10 rounded-full bg-white/20 backdrop-blur-xl border border-white/40 shadow-lg shadow-black/10 transition-all duration-300 group-focus-within:bg-white/30 group-focus-within:border-white/60">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <Search className="h-4 w-4 text-white/70 group-focus-within:text-white transition-colors duration-200" />
                </div>

                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Search recipes..."
                  className="w-full h-full pl-10 pr-11 bg-transparent text-white placeholder:text-white/60 rounded-full text-sm focus:outline-none transition-all duration-300"
                />

                <button
                  onClick={handleSearch}
                  aria-label="Search"
                  className="absolute right-1 h-8 w-8 flex items-center justify-center bg-white/90 hover:bg-white text-primary rounded-full transition-all duration-200 shadow-sm"
                >
                  <Search className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
          </div>

        </main>
      </div>
    </div>
  );
};

export default Landing;
